import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ModifierGroupsService, ModifierOption } from '../modifier-groups.service';

export interface ModifierGroupFormDialogData {
  id?: string;
}

@Component({
  selector: 'app-modifier-group-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title>
        <mat-icon>tune</mat-icon>
        {{ isEditMode ? 'Edit Modifier Group' : 'Create Modifier Group' }}
      </h2>
      <button mat-icon-button type="button" (click)="onCancel()" [disabled]="loading">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content>
      <form [formGroup]="form" (ngSubmit)="onSubmit()" id="groupForm">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Group name</mat-label>
          <input matInput formControlName="name" placeholder="e.g. Size or Extras" required />
          <mat-error>{{ getError('name') }}</mat-error>
        </mat-form-field>

        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Selection type</mat-label>
            <mat-select formControlName="selection_type">
              <mat-option value="single">Single choice</mat-option>
              <mat-option value="multiple">Multiple choice</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Min select</mat-label>
            <input matInput type="number" formControlName="min_select" min="0" />
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Max select</mat-label>
            <input matInput type="number" formControlName="max_select" min="0" />
          </mat-form-field>
        </div>

        <div class="form-section">
          <mat-slide-toggle formControlName="is_required" color="accent">Required</mat-slide-toggle>
        </div>

        <div class="options-header">
          <h3>Options</h3>
          <button type="button" mat-stroked-button (click)="addOption()">
            <mat-icon>add</mat-icon> Add option
          </button>
        </div>

        <div formArrayName="options">
          <div class="option-row" *ngFor="let opt of options.controls; let idx = index" [formGroupName]="idx">
            <mat-form-field appearance="outline" class="option-name">
              <mat-label>Option</mat-label>
              <input matInput formControlName="name" placeholder="e.g. Large" />
            </mat-form-field>
            <mat-form-field appearance="outline" class="option-price">
              <mat-label>Price +/-</mat-label>
              <input matInput type="number" formControlName="price_delta" step="0.01" />
            </mat-form-field>
            <button type="button" mat-icon-button color="warn" (click)="removeOption(idx)" matTooltip="Remove option">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
          <p *ngIf="options.length === 0" class="no-options">No options yet. Add options like Small, Medium, Large.</p>
        </div>

        <div class="form-section">
          <mat-slide-toggle formControlName="is_active" color="accent">Active</mat-slide-toggle>
        </div>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()" [disabled]="loading">Cancel</button>
      <button mat-raised-button color="primary" form="groupForm" type="submit" [disabled]="loading || form.invalid">
        <mat-spinner *ngIf="loading" diameter="20"></mat-spinner>
        <span *ngIf="!loading">{{ isEditMode ? 'Update' : 'Create' }}</span>
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-header { display: flex; align-items: center; justify-content: space-between; }
    .dialog-header h2 { display: flex; align-items: center; gap: 8px; margin: 0; }
    mat-dialog-content { min-width: 600px; padding-top: 8px; }
    .full-width { width: 100%; }
    .form-row { display: flex; gap: 16px; }
    .form-row mat-form-field { flex: 1; }
    .form-section { margin: 8px 0; }
    .options-header { display: flex; align-items: center; justify-content: space-between; margin: 12px 0 4px; }
    .options-header h3 { margin: 0; font-size: 1rem; }
    .option-row { display: flex; gap: 12px; align-items: center; }
    .option-row .option-name { flex: 2; }
    .option-row .option-price { flex: 1; }
    .no-options { color: #64748b; font-size: 0.85rem; margin: 4px 0 12px; }
    mat-dialog-actions button mat-spinner { display: inline-block; margin-right: 8px; }
    @media (max-width: 660px) { mat-dialog-content { min-width: unset; } .form-row { flex-direction: column; gap: 0; } }
  `],
})
export class ModifierGroupFormDialogComponent {
  form: FormGroup;
  loading = false;
  isEditMode: boolean;
  id?: string;

  constructor(
    private fb: FormBuilder,
    private service: ModifierGroupsService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<ModifierGroupFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ModifierGroupFormDialogData,
  ) {
    this.id = data?.id;
    this.isEditMode = !!this.id;
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      selection_type: ['single', Validators.required],
      min_select: [0, [Validators.min(0)]],
      max_select: [1, [Validators.min(0)]],
      is_required: [false],
      is_active: [true],
      options: this.fb.array([]),
    });

    if (this.isEditMode) {
      this.load();
    }
  }

  get options(): FormArray {
    return this.form.get('options') as FormArray;
  }

  private optionGroup(opt?: ModifierOption): FormGroup {
    return this.fb.group({
      id: [opt?.id ?? null],
      name: [opt?.name ?? '', Validators.required],
      price_delta: [opt ? Number(opt.price_delta) : 0],
    });
  }

  addOption(opt?: ModifierOption): void {
    this.options.push(this.optionGroup(opt));
  }

  removeOption(index: number): void {
    this.options.removeAt(index);
  }

  private load(): void {
    if (!this.id) return;
    this.loading = true;
    this.service.get(this.id).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const g = res.data;
          this.form.patchValue({
            name: g.name,
            selection_type: g.selection_type,
            min_select: g.min_select,
            max_select: g.max_select,
            is_required: g.is_required,
            is_active: g.is_active,
          });
          this.options.clear();
          (g.options ?? []).forEach((opt) => this.addOption(opt));
        }
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Failed to load modifier group', 'Close', { duration: 3000 });
        this.loading = false;
        this.dialogRef.close(false);
      },
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading = true;
    const value = this.form.value;
    const payload = {
      ...value,
      options: (value.options ?? []).map((o: any, idx: number) => ({
        id: o.id ?? undefined,
        name: o.name,
        price_delta: Number(o.price_delta) || 0,
        sort_order: idx,
      })),
    };

    const request$ = this.isEditMode && this.id
      ? this.service.update(this.id, payload)
      : this.service.create(payload);

    request$.subscribe({
      next: (res) => {
        if (res.success) {
          this.snackBar.open(`Modifier group ${this.isEditMode ? 'updated' : 'created'} successfully`, 'Close', { duration: 3000 });
          this.dialogRef.close(true);
        }
        this.loading = false;
      },
      error: (error) => {
        this.snackBar.open(error.error?.message || 'Failed to save modifier group', 'Close', { duration: 5000 });
        if (error.error?.errors) {
          Object.keys(error.error.errors).forEach((key) => {
            this.form.get(key)?.setErrors({ serverError: error.error.errors[key][0] });
          });
        }
        this.loading = false;
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  getError(controlName: string): string {
    const control = this.form.get(controlName);
    if (!control?.errors) return '';
    if (control.errors['required']) return 'This field is required';
    if (control.errors['maxlength']) return 'Too long';
    if (control.errors['serverError']) return control.errors['serverError'];
    return 'Invalid value';
  }
}
